/**
 * Shared utilities for hierarchical team dropdowns.
 *
 * These utilities are used across multiple components that display teams in a
 * nested expand/collapse dropdown (TeamMenu, PrFilters, IssueFilters,
 * ContributorTableFilters, TeamFilterSelect, TeamSelect, etc.)
 */

/** Minimal tree-node shape expected by the flatten utility. */
export interface TeamHierarchyNode {
  name: string;
  subTeams?: TeamHierarchyNode[];
}

/** Flattened option produced by flattenTeamsWithMetadata. */
export interface TeamHierarchyOption {
  value: string;
  label: string;
  depth: number;
  teamPath: string;
  hasNestedSubTeams: boolean;
}

/**
 * Recursively flattens a nested team tree into a flat array, enriching each
 * entry with `depth`, `teamPath` and `hasNestedSubTeams` metadata.
 *
 * - `hasNestedSubTeams` is `true` only for non-root (depth > 0) teams that
 *   have children, because root-level children (depth 1) are always shown, so
 *   a root team never needs an expand button.
 * - `visitedPaths` prevents infinite recursion for circular references.
 */
export const flattenTeamsWithMetadata = (
  teams: TeamHierarchyNode[] | undefined,
  depth = 0,
  parentPath = '',
  visitedPaths: Set<string> = new Set()
): TeamHierarchyOption[] => {
  if (!teams || teams.length === 0) return [];

  return teams.flatMap((team) => {
    const teamPath = parentPath ? `${parentPath}/${team.name}` : team.name;

    if (visitedPaths.has(teamPath)) return [];
    visitedPaths.add(teamPath);

    const hasNestedSubTeams = depth > 0 && (team.subTeams?.length ?? 0) > 0;

    const current: TeamHierarchyOption = {
      value: team.name,
      label: team.name,
      depth,
      teamPath,
      hasNestedSubTeams,
    };

    const children = flattenTeamsWithMetadata(
      team.subTeams ?? [],
      depth + 1,
      teamPath,
      visitedPaths
    );

    return [current, ...children];
  });
};

/**
 * Filters a flat team list to only the items that should be visible in the
 * dropdown given the current expand/collapse state.
 *
 * Rules:
 *   depth 0 → always visible
 *   depth 1 → always visible
 *   depth ≥ 2 → visible only if every ancestor at depth ≥ 1 is expanded
 */
export const filterVisibleTeamOptions = <
  T extends { depth?: number; teamPath?: string }
>(
  allTeams: T[],
  expandedTeamPaths: Set<string>
): T[] => {
  return allTeams.filter((team) => {
    const depth = team.depth ?? 0;

    if (depth <= 1) return true;

    if (!team.teamPath || typeof team.teamPath !== 'string') return false;

    const pathParts = team.teamPath.split('/');
    for (let i = 2; i < pathParts.length; i++) {
      const parentPath = pathParts.slice(0, i).join('/');
      if (!expandedTeamPaths.has(parentPath)) return false;
    }
    return true;
  });
};
